import React, { useState } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import UsersPlannedStep from './steps/UsersPlannedStep';
import GoalsStep from './steps/GoalsStep';
import RecommendationScreen from './RecommendationScreen';
import ContactGate from './ContactGate';
import PriceReveal from './PriceReveal';

const PricingWizard = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    usersPlanned: '',
    goals: [],
  });
  const [recommendation, setRecommendation] = useState(null);
  const [showContactGate, setShowContactGate] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const totalSteps = 2;

  const steps = [
    { component: UsersPlannedStep, key: 'usersPlanned' },
    { component: GoalsStep, key: 'goals' },
  ];

  const updateData = (key, value) => {
    setWizardData(prev => ({ ...prev, [key]: value }));
  };

  const calculateRecommendation = () => {
    const { usersPlanned } = wizardData;

    // Simple tier-based recommendation
    if (usersPlanned === '500+') {
      return {
        package: 'Enterprise',
        tier: 'enterprise',
        pricePerUser: null,
        userRange: '500+',
        reasons: [
          'Custom enterprise solutions for complex organizations',
          'Volume-based pricing tailored to your scale',
          'Dedicated onboarding & enablement team',
          'Strategic partnership support'
        ],
        features: [
          'Everything in Scaling, plus:',
          'Volume-based pricing',
          'Dedicated onboarding & enablement',
          'Custom training and role-play development',
          'White-labeling options',
          'Advanced reporting & integrations',
          'Optional professional media production',
          'Strategic partnership support'
        ]
      };
    }

    if (usersPlanned === '100-499') {
      return {
        package: 'Scaling Organizations',
        tier: 'scaling',
        pricePerUser: 15,
        userRange: '100-499',
        reasons: [
          'Built for larger teams that need consistency at scale',
          'Advanced analytics and manager-level insights',
          'Priority support for faster issue resolution',
          'Governance tools for multi-department consistency'
        ],
        features: [
          'Everything in Starter, plus:',
          'Advanced analytics & reporting',
          'Team and manager-level performance insights',
          'Expanded role-play and training management',
          'Governance and consistency across departments',
          'Priority support'
        ]
      };
    }

    // Default: Starter (1-99 users)
    return {
      package: 'Starter Teams',
      tier: 'starter',
      pricePerUser: 20,
      userRange: '1-99',
      reasons: [
        'Everything your team needs to train faster and perform better',
        'Full access to Traain Engine and Cadence',
        'Perfect for growing teams ready to modernize enablement',
        'Simple per-user pricing with no hidden fees'
      ],
      features: [
        'Traain Engine (AI-powered training creation & delivery)',
        'Traain Cadence (AI role-play with real-time feedback)',
        'Navigation AI Layer (guidance, scoring, and insights)',
        'Training libraries & role-play assignments',
        'Performance dashboards & completion tracking',
        'Standard onboarding & support'
      ]
    };
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Wizard complete - show recommendation
      const rec = calculateRecommendation();
      setRecommendation(rec);
      setCurrentStep(totalSteps);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleShowPrice = () => {
    setShowContactGate(true);
  };

  const handleContactSubmit = () => {
    setShowContactGate(false);
    setShowPricing(true);
  };

  const canProceed = () => {
    if (currentStep === 0) return wizardData.usersPlanned !== '';
    if (currentStep === 1) return wizardData.goals.length > 0;
    return true;
  };

  // Show pricing reveal
  if (showPricing) {
    return <PriceReveal recommendation={recommendation} wizardData={wizardData} />;
  }

  // Show contact gate
  if (showContactGate) {
    return <ContactGate recommendation={recommendation} wizardData={wizardData} onSubmit={handleContactSubmit} />;
  }

  // Show recommendation screen
  if (currentStep === totalSteps) {
    return <RecommendationScreen recommendation={recommendation} onShowPrice={handleShowPrice} />;
  }

  // Show current wizard step
  const CurrentStepComponent = steps[currentStep].component;
  const currentKey = steps[currentStep].key;

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl sm:rounded-2xl p-4 sm:p-8">
      {/* Progress bar */}
      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs sm:text-sm text-gray-400">Step {currentStep + 1} of {totalSteps}</span>
          <span className="text-xs sm:text-sm text-gray-400">{Math.round(((currentStep + 1) / totalSteps) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2">
          <div 
            className="bg-cyan-500 h-1.5 sm:h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Current step */}
      <CurrentStepComponent
        value={wizardData[currentKey]}
        onChange={(value) => updateData(currentKey, value)}
      />

      {/* Navigation */}
      <div className="flex justify-between mt-6 sm:mt-8 gap-4">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0}
          className="border-gray-600 text-gray-300 hover:bg-gray-700 text-sm sm:text-base"
          data-testid="pricing-wizard-back-btn"
        >
          <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!canProceed()}
          className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          data-testid="pricing-wizard-next-btn"
        >
          {currentStep === totalSteps - 1 ? 'See My Plan' : 'Next'}
          <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default PricingWizard;
